import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import Modal from './Modal';
import {
  ActionBar,
  Badge,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Muted,
  PageActions,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Table,
  TableWrap,
  TextArea,
  Toast,
  Toolbar,
} from './ui';

interface TimerItem {
  _id: string;
  name: string;
  message: string;
  intervalMinutes: number;
  active: boolean;
  lastSentAt?: string;
}

const emptyForm = {
  name: '',
  message: '',
  intervalMinutes: 5,
  active: true,
};

const TimersPanel: React.FC = () => {
  const [timers, setTimers] = useState<TimerItem[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/timers');
    setTimers(data);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return timers;
    return timers.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      t.message.toLowerCase().includes(q),
    );
  }, [timers, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (timer: TimerItem) => {
    setEditingId(timer._id);
    setForm({
      name: timer.name,
      message: timer.message,
      intervalMinutes: timer.intervalMinutes,
      active: timer.active,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await api.put(`/timers/${editingId}`, form);
      setToast('Timer actualizado');
    } else {
      await api.post('/timers', form);
      setToast('Timer creado');
    }
    closeModal();
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('¿Eliminar este timer?')) return;
    await api.delete(`/timers/${id}`);
    setToast('Timer eliminado');
    load();
  };

  const toggle = async (id: string) => {
    await api.patch(`/timers/${id}/toggle`);
    load();
  };

  const testSend = async (id: string) => {
    try {
      await api.post(`/timers/${id}/test`);
      setToast('Mensaje de prueba enviado al chat');
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setToast(msg || 'No se pudo enviar');
    }
  };

  return (
    <>
      {toast && <Toast>{toast}</Toast>}

      <PageActions style={{ marginBottom: '0.5rem', justifyContent: 'flex-end' }}>
        <Button type="button" onClick={openCreate}>+ Nuevo timer</Button>
      </PageActions>

      <Surface>
        <SurfaceHeader>
          <Toolbar style={{ flex: 1 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar timer..."
              style={{ maxWidth: 320 }}
            />
            <Muted style={{ margin: 0 }}>{filtered.length}/{timers.length}</Muted>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody $flush>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '16%' }}>Nombre</th>
                  <th>Mensaje</th>
                  <th style={{ width: '9%' }}>Cada</th>
                  <th style={{ width: '14%' }}>Estado</th>
                  <th style={{ width: '22%', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t._id}>
                    <td>{t.name}</td>
                    <td style={{ maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.message}>{t.message}</td>
                    <td>{t.intervalMinutes} min</td>
                    <td>
                      <Badge $active={t.active}>{t.active ? 'Activo' : 'Pausado'}</Badge>
                      {t.lastSentAt && (
                        <div style={{ fontSize: '0.68rem', marginTop: '0.2rem', opacity: 0.75 }}>
                          {new Date(t.lastSentAt).toLocaleString('es-AR')}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <ActionBar>
                        <Button $variant="ghost" $size="sm" type="button" onClick={() => openEdit(t)}>Editar</Button>
                        <Button $size="sm" type="button" onClick={() => testSend(t._id)}>Probar</Button>
                        <Button $variant="ghost" $size="sm" type="button" onClick={() => toggle(t._id)}>{t.active ? 'Pausar' : 'Activar'}</Button>
                        <Button $variant="danger" $size="sm" type="button" onClick={() => remove(t._id)}>×</Button>
                      </ActionBar>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin timers</td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </SurfaceBody>
      </Surface>

      <Modal
        open={modalOpen}
        title={editingId ? 'Editar timer' : 'Nuevo timer'}
        onClose={closeModal}
        footer={(
          <>
            <Button $variant="ghost" type="button" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" form="timer-form">{editingId ? 'Guardar' : 'Crear'}</Button>
          </>
        )}
      >
        <form id="timer-form" onSubmit={save}>
          <FormGrid>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </FormRow>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Mensaje en chat</Label>
              <TextArea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={4} />
              <Muted>Variable opcional: {'{discord}'}</Muted>
            </FormRow>
            <FormRow>
              <Label>Intervalo (minutos)</Label>
              <Input type="number" min={1} value={form.intervalMinutes} onChange={(e) => setForm({ ...form, intervalMinutes: parseInt(e.target.value, 10) || 5 })} />
            </FormRow>
            <FormRow style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#b8a9c9', fontSize: '0.84rem' }}>
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Activo al guardar
              </label>
            </FormRow>
          </FormGrid>
        </form>
      </Modal>
    </>
  );
};

export default TimersPanel;
